import { useEffect } from 'react';
import { useAuth } from '@/lib/hooks/use-auth';
import { updateUserProfile } from '@/lib/profile';
import { notifyWelcome } from '@/lib/notifications-helpers';

export function UserSync() {
  const { user, loading } = useAuth();
  useEffect(() => {
    const syncUser = async () => {
      if (loading || !user) return;

      try {
        // First, check if profile exists and get the synced data
        const { getUserProfile } = await import('@/lib/profile');
        const existingProfile = await getUserProfile(user.id, user.email || undefined);
        
        // Sync user to backend API
        const email = user.email || '';
        
        // Build update object - sync from auth provider to database
        const updates: any = {
          email: email, // Always sync email
        };
        
        // Sync username from auth provider if available
        // Only overwrite if profile doesn't have one OR if auth provider has a different one
        if (user.username) {
          if (!existingProfile?.username || existingProfile.username !== user.username) {
            updates.username = user.username;
          }
        }
        
        // Sync names from auth provider - update if missing or different
        const authFirstName = user.firstName || null;
        const authLastName = user.lastName || null;
        
        if (authFirstName && (!existingProfile?.first_name || existingProfile.first_name !== authFirstName)) {
          updates.first_name = authFirstName;
        }
        if (authLastName && (!existingProfile?.last_name || existingProfile.last_name !== authLastName)) {
          updates.last_name = authLastName;
        }
        
        // Sync image_url from auth provider ONLY if profile doesn't have one
        // This preserves user-uploaded profile pictures
        if (!existingProfile?.image_url && null) {
          updates.image_url = null;
        }
        
        // Only sync if there are updates to make
        const isNewUser = !existingProfile;
        if (Object.keys(updates).length > 1 || (updates.email && !existingProfile)) {
          const success = await updateUserProfile(user.id, updates, email);

          if (success) {
            // Create welcome notification for new users
            if (isNewUser) {
              try {
                await notifyWelcome(user.id);
              } catch (notifError) {
                console.error('Error creating welcome notification:', notifError);
                // Don't fail user sync if notification fails
              }
            }
          }
        }
      } catch (error: any) {
        // Extract error information safely
        const errorMessage = error?.message || error?.toString() || 'Unknown sync error';
        const errorCode = error?.code || 'UNKNOWN';
        
        // Only log if it's a real error (not just a duplicate)
        if (errorCode !== '23505') { // 23505 = unique violation (duplicate)
          console.error('User sync error:', errorMessage, errorCode);
        }
      }
    };

    syncUser();
  }, [user, loading]);

  // This component doesn't render anything, it just handles syncing
  return null;
}

